import {type EventDataType} from '../../types';

const DEFAULT_BUFFER_LIMIT = 1000;

/**
 * Holds `message` events that reach the Canvas App window before
 * `useReceivedProjectData` had a chance to subscribe.
 *
 * The Designer post-messages whole project data right after the iframe
 * `load` event, and afterwards only posts keys whose value changed.
 *
 * On the Canvas App end, a hook subscribes from an effect, which React runs some time after the
 * first commit. This means that a message delivered in between is dropped and never repeated, which
 * leads to the cross-origin app staying in a loading state.
 *
 * This class handles buffering messages from the Designer until Canvas App is ready to receive them.
 * The Canvas App entrypoint calls `install()` before the `load` event can fire.
 * `drain()` is called once on subscribe, processing buffered messages.
 */
export class DesignerDataMessageBuffer {
  private target: Window | null = null;
  private buffer: Array<MessageEvent<EventDataType>> | null = null;
  private listener: ((event: MessageEvent<EventDataType>) => void) | null =
    null;

  constructor(private readonly limit: number = DEFAULT_BUFFER_LIMIT) {}

  /** Start buffering `message` events on `target` window */
  install(target: Window = window): void {
    if (this.listener) {
      return;
    }
    const buffer: Array<MessageEvent<EventDataType>> = [];
    const listener = (event: MessageEvent<EventDataType>) => {
      // Bounded so a Canvas App that never mounts cannot grow this forever.
      if (buffer.length < this.limit) {
        buffer.push(event);
      }
    };
    this.target = target;
    this.buffer = buffer;
    this.listener = listener;
    target.addEventListener('message', listener);
  }

  /** Stop buffering and drop anything buffered. */
  private uninstall(): void {
    if (this.target && this.listener) {
      this.target.removeEventListener('message', this.listener);
    }
    this.target = null;
    this.listener = null;
    this.buffer = null;
  }

  /** Stop buffering and hand every buffered message to `callback`, in order. */
  drain(callback: (event: MessageEvent<EventDataType>) => void): void {
    const buffered = this.buffer;
    this.uninstall();
    buffered?.forEach(callback);
  }
}

/**
 * Shared instance: installed by the Canvas App entrypoint, drained by
 * `useReceivedProjectData`.
 */
export const designerDataMessageBuffer = new DesignerDataMessageBuffer();
